import type { FC } from "react";
import { Pressable, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Icon } from "../Icon";
import type { IconName } from "../../icons/icon-names";
import { useViewColor, type ViewColorPalette } from "../../hooks/use-view-color";
import { Text } from "../Text";

export interface ChipProps {
  readonly label: string;
  readonly disabled?: boolean | undefined;
  /** Visual variant. "filled" uses a solid background; "outlined" uses a border with transparent background. */
  readonly variant?: "filled" | "outlined" | undefined;
  /**
   * Background of a `"filled"` chip, as a theme colour name. Defaults to
   * `"muted"`. Pick one that contrasts with the surface the chip sits on.
   */
  readonly color?: ViewColorPalette | undefined;
  /** Makes the entire chip pressable. */
  readonly onPress?: (() => void) | undefined;
  /** Renders a trailing close icon that removes the chip. Shorthand for a delete action. */
  readonly onDelete?: (() => void) | undefined;
  /** Trailing icon name. Defaults to "close" when onDelete is set, "arrowRight" when onPress is set. */
  readonly trailingIcon?: IconName | undefined;
}

const Chip: FC<ChipProps> = ({
  label,
  onPress,
  onDelete,
  trailingIcon,
  variant = "filled",
  color = "muted",
  disabled = false,
}) => {
  const { theme } = useUnistyles();
  const resolveColor = useViewColor();

  const resolvedIcon: IconName | undefined =
    trailingIcon ??
    (onDelete ? "close"
    : onPress ? "arrowRight"
    : undefined);

  const content = (
    <View
      style={[
        styles.chip,
        variant === "outlined" ? styles.chipOutlined : { backgroundColor: resolveColor(color) },
        disabled && styles.chipDisabled,
      ]}
    >
      <Text textStyle="xs/medium" color="text" numberOfLines={1} style={styles.label}>
        {label}
      </Text>
      {resolvedIcon &&
        !disabled &&
        (onDelete && !onPress ?
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            hitSlop={4}
            accessibilityRole="button"
            accessibilityLabel={`Remove ${label}`}
          >
            <Icon name={resolvedIcon} size={14} color={theme.colors.textMuted} />
          </Pressable>
        : <Icon name={resolvedIcon} size={14} color={theme.colors.textMuted} />)}
    </View>
  );

  if (onPress && !disabled) {
    return (
      <Pressable
        onPress={(e) => {
          e.stopPropagation();
          onPress();
        }}
        accessibilityRole="button"
        style={{ alignSelf: "flex-start", maxWidth: "100%" }}
      >
        {content}
      </Pressable>
    );
  }

  return content;
};

Chip.displayName = "Chip";

export { Chip };

const styles = StyleSheet.create((theme) => ({
  chip: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    maxWidth: "100%",
    flexShrink: 1,
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.spacing.lg,
  },
  chipOutlined: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  chipDisabled: {
    opacity: 0.5,
  },
  label: {
    flexShrink: 1,
    lineHeight: 16,
    includeFontPadding: false,
  },
}));
